import { api, errorBanner, loadingBanner } from "../api.js";
import { replaceQuery } from "../router.js";
import { escapeHtml } from "../format.js";
import { renderPagination, bindPagination } from "../components/pagination.js";
import { renderHouseTabs, bindHouseTabs } from "../components/segmented.js";
import { renderFilterFooter, isDirty } from "../components/filterbar.js";
import { renderPropositionCard } from "../components/propcard.js";

const TYPES = ["PL", "PLP", "PEC", "MPV", "PDL", "PLV", "PLS", "PLC", "PRC", "PRS"];
const DEFAULTS = { house: "", type: "", year: "", subject: "", has_votes: false };

export async function renderPropositions({ root, query, isCurrent }) {
  document.title = "Proposições · Como Vota";
  const state = {
    house: query.house || "",
    type: query.type || "",
    year: query.year || "",
    subject: query.subject || "",
    has_votes: query.has_votes === "1" || query.has_votes === "true",
    sort: query.sort || "recent",
    page: Number(query.page || 1),
  };
  let subjects = [];
  try {
    subjects = await api.subjects();
  } catch {
    subjects = [];
  }
  if (!isCurrent()) return;

  root.innerHTML = `
    <section class="detail detail--wide">
      <div class="page-head">
        <span class="eyebrow-pill">Propostas</span>
        <p class="page-head__kicker">Proposições e propostas</p>
        <h1 class="page-title" data-page-title>${state.sort === "voted" ? "Em votação" : "Proposições"}</h1>
        <p class="muted">Projetos de lei, PECs, medidas provisórias e outras matérias das duas Casas. Clique para ver o resumo, o texto completo e a linha do tempo.</p>
      </div>
      ${renderHouseTabs(state.house)}
      <form class="filters" data-form>
        <div class="filters__row">
          <label>Tipo
            <select name="type">
              <option value="">Todos</option>
              ${TYPES.map((t) => `<option value="${t}" ${state.type === t ? "selected" : ""}>${t}</option>`).join("")}
            </select>
          </label>
          <label>Tema
            <select name="subject">
              <option value="">Todos</option>
              ${subjects.map((s) => `<option value="${escapeHtml(s.slug)}" ${state.subject === s.slug ? "selected" : ""}>${escapeHtml(s.label)}</option>`).join("")}
            </select>
          </label>
          <label>Ano <input type="number" name="year" min="1900" max="2100" value="${escapeHtml(state.year)}" placeholder="AAAA" /></label>
          <label>Ordenar
            <select name="sort">
              <option value="recent" ${state.sort === "recent" ? "selected" : ""}>Atualização recente</option>
              <option value="voted" ${state.sort === "voted" ? "selected" : ""}>Votadas recentemente</option>
              <option value="presented" ${state.sort === "presented" ? "selected" : ""}>Apresentação</option>
            </select>
          </label>
          <label class="check"><input type="checkbox" name="has_votes" ${state.has_votes ? "checked" : ""} /> Só com votações</label>
        </div>
        <div class="chips" data-chips></div>
        <div class="filters__foot" data-foot></div>
      </form>
      <div data-results>${loadingBanner()}</div>
    </section>`;

  const form = root.querySelector("[data-form]");
  const foot = root.querySelector("[data-foot]");
  const chips = root.querySelector("[data-chips]");
  const results = root.querySelector("[data-results]");
  const subjectLabel = (slug) => subjects.find((s) => s.slug === slug)?.label || slug;
  form.addEventListener("submit", (e) => e.preventDefault());
  chips.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-clear]");
    if (!btn) return;
    const key = btn.dataset.clear;
    if (key === "house") {
      state.house = "";
      root.querySelectorAll("[data-house]").forEach((b) => b.classList.toggle("segmented__btn--active", b.dataset.house === ""));
    } else if (key === "has_votes") {
      state.has_votes = false;
      form.has_votes.checked = false;
    } else {
      state[key] = "";
      form[key].value = "";
    }
    state.page = 1;
    load();
  });

  function renderChips() {
    const items = [
      state.house ? { key: "house", label: state.house === "camara" ? "Câmara" : "Senado", cls: "chip--dark" } : null,
      state.type ? { key: "type", label: state.type, cls: "chip--green" } : null,
      state.subject ? { key: "subject", label: subjectLabel(state.subject), cls: "" } : null,
      state.year ? { key: "year", label: state.year, cls: "chip--green" } : null,
      state.has_votes ? { key: "has_votes", label: "Só com votações", cls: "" } : null,
    ].filter(Boolean);
    chips.innerHTML = items
      .map((c) => `<button type="button" class="chip ${c.cls}" data-clear="${c.key}" title="Remover filtro">${escapeHtml(c.label)} <span class="chip__x">✕</span></button>`)
      .join("");
  }
  form.querySelectorAll("select, input").forEach((el) =>
    el.addEventListener("change", () => {
      state.type = form.type.value;
      state.subject = form.subject.value;
      state.year = form.year.value;
      state.sort = form.sort.value;
      state.has_votes = form.has_votes.checked;
      state.page = 1;
      load();
    }),
  );
  bindHouseTabs(root, (house) => {
    state.house = house;
    state.page = 1;
    load();
  });

  function footer(total, loading) {
    foot.innerHTML = renderFilterFooter({
      total,
      loading,
      noun: "proposição",
      nounPlural: "proposições",
      hasFilters: isDirty(state, DEFAULTS),
      clearHref: state.sort !== "recent" ? `/proposicoes?sort=${state.sort}` : "/proposicoes",
    });
  }

  async function load() {
    replaceQuery({ ...state, has_votes: state.has_votes ? "1" : "" });
    root.querySelector("[data-page-title]").textContent = state.sort === "voted" ? "Em votação" : "Proposições";
    renderChips();
    results.innerHTML = loadingBanner();
    footer(null, true);
    try {
      const page = await api.propositions({
        house: state.house,
        type: state.type,
        subject: state.subject,
        year: state.year,
        has_votes: state.has_votes ? "true" : "",
        sort: state.sort,
        page: state.page,
        page_size: 20,
      });
      if (!isCurrent()) return;
      footer(page.total, false);
      const dateField = state.sort === "voted" ? "last_roll_call_at" : null;
      results.innerHTML = page.items?.length
        ? `<ul class="card-grid">${page.items.map((p) => renderPropositionCard(p, { dateField })).join("")}</ul>${renderPagination(page)}`
        : `<div class="empty-card">Nenhuma proposição encontrada.</div>`;
      bindPagination(results, (n) => {
        state.page = n;
        load();
      });
    } catch (err) {
      if (!isCurrent()) return;
      footer(null, false);
      results.innerHTML = errorBanner(err);
    }
  }
  await load();
}
